// src/components/admin/AccountCheck.js
//
// Admin Roles ▸ 🩺 Account check. Type an email → see whether the account is
// a student or a teacher, every field that decides it, and WHY a student
// ended up as a teacher. "Make this a student account" fixes it
// (functions/accountDoctor.js).

import React, { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

const box = { margin: "14px 0", padding: 16, borderRadius: 16, background: "#f5f3ff", border: "2px solid #ddd6fe", fontFamily: "inherit" };
const row = { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" };
const btn = { border: 0, borderRadius: 999, padding: "8px 16px", fontWeight: 800, cursor: "pointer", background: "#7c3aed", color: "#fff" };
const soft = { ...btn, background: "#fff", color: "#5b21b6", boxShadow: "inset 0 0 0 2px #c4b5fd" };

const AccountCheck = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const [msg, setMsg] = useState(null);

  const check = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setMsg(null);
    setInfo(null);
    try {
      const r = await httpsCallable(getFunctions(), "adminCheckAccount")({ email: email.trim() });
      setInfo(r.data);
    } catch (err) {
      setMsg({ bad: true, text: err?.message || "Couldn't check that account." });
    } finally {
      setBusy(false);
    }
  };

  const fix = async () => {
    if (!info) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await httpsCallable(getFunctions(), "adminMakeStudent")({ uid: info.uid });
      setMsg({ text: `Done — ${info.name || info.email} is a student account again (role "${r.data.role}"). They'll see the student pages next time they open the site.` });
      const again = await httpsCallable(getFunctions(), "adminCheckAccount")({ email: info.email });
      setInfo(again.data);
    } catch (err) {
      setMsg({ bad: true, text: err?.message || "Couldn't change it." });
    } finally {
      setBusy(false);
    }
  };

  const Field = ({ k, v }) => (
    <div style={{ padding: "6px 10px", background: "#fff", borderRadius: 10, minWidth: 140 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: "#7c3aed", textTransform: "uppercase" }}>{k}</div>
      <div style={{ fontWeight: 700, wordBreak: "break-all" }}>{v || "—"}</div>
    </div>
  );

  return (
    <div style={box}>
      <h3 style={{ margin: "0 0 4px" }}>🩺 Account check</h3>
      <p style={{ margin: "0 0 10px", color: "#4c1d95" }}>Is someone showing up as the wrong kind of account (a student who became a teacher)? Type their sign-in email.</p>
      <form style={row} onSubmit={check}>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="student@email.com" style={{ flex: "1 1 240px", padding: "8px 12px", borderRadius: 10, border: "2px solid #c4b5fd" }} />
        <button type="submit" style={btn} disabled={busy || !email.trim()}>
          {busy ? "Checking…" : "Check"}
        </button>
      </form>

      {info && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 18, fontWeight: 900, margin: "0 0 8px" }}>
            {info.name || info.email} is {info.looksTeacher ? "a 🧑‍🏫 TEACHER account" : "a 🎒 student account"}
          </p>
          <div style={row}>
            <Field k="Role" v={info.role} />
            <Field k="Signed up as" v={info.intendedRole} />
            <Field k="Class code" v={info.educatorCode && `${info.educatorCode}${info.ownsCode ? " (their own)" : info.codeOwner ? ` (${info.codeOwner}'s class)` : ""}`} />
            <Field k="Plan" v={info.plan || (info.seat ? "a seat from someone's purchase" : "")} />
            <Field k="State / grade" v={[info.state, info.grade].filter(Boolean).join(" · ")} />
            {info.previousRole && <Field k="Role before" v={info.previousRole} />}
          </div>
          {info.reasons?.length > 0 && (
            <ul style={{ margin: "10px 0", paddingLeft: 20 }}>
              {info.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          )}
          {info.looksTeacher && (
            <button type="button" style={btn} disabled={busy} onClick={fix}>
              Make this a student account
            </button>
          )}{" "}
          <button type="button" style={soft} onClick={() => setInfo(null)}>
            Close
          </button>
        </div>
      )}
      {msg && <p style={{ margin: "10px 0 0", fontWeight: 800, color: msg.bad ? "#b91c1c" : "#15803d" }}>{msg.text}</p>}
    </div>
  );
};

export default AccountCheck;
